import { startOfDay, addDays } from "date-fns";
import type { CalendarEvent } from "./record-calendar-model";

/** What indexing reads of an event: its period. */
type CalendarIndexedEvent = Pick<CalendarEvent, "start" | "end" | "allDay">;

export type CalendarDayEvents<T extends CalendarIndexedEvent = CalendarEvent> = {
  allDay: T[];
  timed: T[];
};

export function calendarEventsByDay<T extends CalendarIndexedEvent>(
  events: readonly T[],
  from: Date,
  to: Date,
): Map<string, CalendarDayEvents<T>> {
  const indexed = new Map<string, CalendarDayEvents<T>>();
  const days: Array<{ start: number; key: string }> = [];
  const lastDay = startOfDay(to);
  for (let day = startOfDay(from); day <= lastDay; day = addDays(day, 1)) {
    days.push({ start: day.getTime(), key: calendarDayKey(day) });
  }
  if (!days.length) return indexed;
  const rangeEnd = addDays(lastDay, 1).getTime();
  // Construct zoned day boundaries once, not once for every record. Binary
  // search also keeps long-running events bounded to the displayed period.
  const dayIndex = (instant: number) => {
    let low = 0;
    let high = days.length;
    while (low < high) {
      const middle = (low + high) >>> 1;
      if (days[middle]!.start <= instant) low = middle + 1;
      else high = middle;
    }
    return low - 1;
  };
  for (const event of events) {
    const start = event.start.getTime();
    const end = event.end.getTime() - (event.allDay ? 0 : 1);
    if (start >= rangeEnd || end < days[0]!.start) continue;
    const first = Math.max(0, dayIndex(start));
    const last = dayIndex(end);
    for (let index = first; index <= last; index++) {
      const key = days[index]!.key;
      const bucket = indexed.get(key) ?? { allDay: [], timed: [] };
      bucket[event.allDay ? "allDay" : "timed"].push(event);
      indexed.set(key, bucket);
    }
  }
  return indexed;
}

export function calendarEventsByStartDay(
  events: CalendarEvent[],
): Array<{ day: Date; events: CalendarEvent[] }> {
  const indexed = new Map<string, { day: Date; events: CalendarEvent[] }>();
  for (const event of events) {
    const day = startOfLocalDay(event.start);
    const key = calendarDayKey(day);
    const bucket = indexed.get(key) ?? { day, events: [] };
    bucket.events.push(event);
    indexed.set(key, bucket);
  }
  return [...indexed.values()];
}

export function calendarDayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function startOfLocalDay(date: Date): Date {
  return startOfDay(date);
}
